import React, { useMemo, useState } from "react";
import {
  AppPage,
  MimaButton,
  MimaDropdown,
  MimaOptionMenu,
  MimaTable,
  MimaText,
  PageLoader,
} from "../../../components";
import constant from "../../../utils/constant";
import { Formik } from "formik";
import { adminStore } from "../../../stores";
import styles from "../../../assets/styles/Competition/Competition.module.scss";
import MimaCheckboxTable from "../../../components/MimaTable/MimaCheckboxTable";
import { AnimatePresence } from "framer-motion";
import {
  CreateVotesContainer,
  UpdateParticipantsContainer,
} from "../../../containers";
import { downloadXlsx2 } from "../../../utils/download";
import ViewMoreParticipantDetails from "./ViewMoreParticipantDetails";

const Participants = ({
  onLimitChange,
  data,
  currentPage,
  setCurrentPage,
  limit,
  isLoading,
  isFetching,
  setSearchQuery,
  validationSchema,
  onSubmit,
  setCompetition,
  setStage,
  loading,
  updateParticipantModal,
  goToUpdateParticipantModal,
  closeModal,
  createVotesModal,
  goToCreateVotesModal,
  goToViewMoreModal,
  viewMoreModal,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState({});
  const competitionsArray = adminStore.competitions;
  const tableData = useMemo(() => {
    if (data?.status === constant.Success) {
      return data?.data?.participants || [];
    }
    return [];
  }, [data]);

  const tableColumns = [
    {
      Header: "Competition",
      Cell: ({ cell }) => {
        const competitionNmae = cell.row.original.competition.name;
        return <span>{competitionNmae}</span>;
      },
    },
    {
      Header: "Participant",
      Cell: ({ cell }) => {
        const firstName = cell.row.original.firstname;
        const lastName = cell.row.original.lastname;

        return <span>{`${firstName} ${lastName}`}</span>;
      },
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Mobile",
      accessor: "mobile",
    },
    {
      Header: "Votes",
      accessor: "votes",
    },
    {
      Header: "Stage",
      accessor: "stage",
    },
    {
      Header: "Actions",
      Cell: ({ cell }) => {
        const item = cell.row.original;
        return (
          <MimaOptionMenu
            options={[
              { label: "Create Votes", value: "Create Votes" },
              { label: "View More", value: "View More" },
            ]}
            onOptionSelected={(option) => {
              setSelectedParticipant(item);
              if (option.value === "Create Votes") {
                goToCreateVotesModal();
              } else if (option.value === "View More") {
                goToViewMoreModal();
              }
            }}
          />
        );
      },
    },
  ];

  const printData = tableData.map((item) => ({
    Competition: item?.competition.name,
    "Participant Name": `${item?.firstname} ${item?.lastname}` || "",
    Email: item.email,
    Mobile: item.mobile,
    "Competition stage": item.competition.stage,
    "Stage One Votes": item.stageOneVotes,
    "Stage Two Votes": item.stageTwoVotes,
  }));

  const handleSelectedRowsChange = (newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  };
  return (
    <AppPage pageTitle="Participants">
      <div>
        <Formik
          initialValues={{
            competition: "",
            stage: "",
            participant: "",
          }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            values,
            errors,
            touched,
          }) => (
            <>
              <div className={styles.votesPage}>
                <MimaText variant="bodyBold" mb={2}>
                  Filter Participants Table
                </MimaText>
                <MimaDropdown
                  labelTitle="Competitions"
                  name="competition"
                  id="competition"
                  value={values.competition}
                  placeholder="Select Competition"
                  currentValue={values.competition}
                  onChange={(data) => {
                    setFieldValue("competition", data);
                    setCompetition(data);
                  }}
                  touched={touched.competition}
                  error={errors.competition}
                  data={competitionsArray}
                  width={30}
                />

                <MimaDropdown
                  labelTitle="Competition stage"
                  name="stage"
                  value={values.stage}
                  placeholder="Select Competition stage"
                  id="stage"
                  currentValue={values.stage}
                  onChange={(data) => {
                    setFieldValue("stage", data.key);
                    setStage(data.key);
                  }}
                  data={[
                    { value: "REG", key: "REG" },
                    { value: "ONE", key: "ONE" },
                    { value: "TWO", key: "TWO" },
                    { value: "END", key: "END" },
                  ]}
                  width={30}
                  touched={touched.stage}
                  error={errors.stage}
                />
                <MimaButton
                  title="Filter"
                  width={30}
                  buttonColor="var(--color-dark-4)"
                  onClick={() => {
                    handleSubmit();
                  }}
                  loading={loading}
                />
              </div>
            </>
          )}
        </Formik>

        {isLoading ? (
          <PageLoader title="while we load your information" />
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <MimaButton
                title="Update Participants"
                mt={2}
                onClick={() => {
                  goToUpdateParticipantModal();
                }}
                loading={loading}
              />
              <MimaButton
                title={`Download Report`}
                mt={2}
                onClick={() => {
                  downloadXlsx2(printData);
                }}
              />
            </div>
            <MimaCheckboxTable
              tableData={tableData}
              tableColumns={tableColumns}
              searchPlaceholder="Search Payout"
              searchVariant="wide"
              totalItems={data?.data?.totalCounts}
              onLimitChange={onLimitChange}
              isLoading={isLoading}
              isFetching={isFetching}
              limit={limit}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setSearchQuery={setSearchQuery}
              selectedRows={selectedRows}
              onSelectedRowsChange={handleSelectedRowsChange}
            />
          </div>
        )}

        {(updateParticipantModal || createVotesModal || viewMoreModal) && (
          <div className="modal">
            <AnimatePresence>
              {updateParticipantModal && (
                <UpdateParticipantsContainer
                  closeModal={closeModal}
                  selectedRows={selectedRows}
                />
              )}
              {createVotesModal && (
                <CreateVotesContainer
                  closeModal={closeModal}
                  selectedParticipant={selectedParticipant}
                />
              )}
              {viewMoreModal && (
                <ViewMoreParticipantDetails
                  closeModal={closeModal}
                  selectedParticipant={selectedParticipant}
                />
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </AppPage>
  );
};
export default Participants;
